<template>
  <div class="s-pagination">
    <div class="s-pagination__entry">
      <div class="text-label2 text-grey-darken-3">{{ $t("pagination.page") }}</div>
      <KSelect :model-value="store.pagination.page" name="page" class="s-pagination__select" :items="pageItems" @update:model-value="setPage" />
    </div>
    <div class="s-pagination__entry">
      <div class="text-label2 text-grey-darken-3">{{ $t("pagination.entriesPerPage") }}</div>
      <KSelect :model-value="store.pagination.perPage" name="perPage" class="s-pagination__select" :items="[10, 20, 50, 100]" @update:model-value="setPerPage" />
    </div>
    <div class="s-pagination__entry text-body text-grey-darken-3">
      <div>
        <span class="text-monospace">{{ firstEntryIndex }}</span>
        -
        <span class="text-monospace">{{ lastEntryIndex }}</span>
        {{ $t("pagination.of") }}
        <span class="text-monospace">{{ store.pagination.totalCount }}</span>
      </div>
    </div>
    <div class="s-pagination__entry">
      <k-button icon variant="plain" :disabled="store.pagination.page <= 1" @click="prevPage"><v-icon>far fa-chevron-left</v-icon></k-button>
      <k-button icon variant="plain" :disabled="store.pagination.page >= totalPages" @click="nextPage"><v-icon>far fa-chevron-right</v-icon></k-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { EntryListStore } from "@/store/factories/entryListStore"
import { computed, toRef } from "vue"

const props = defineProps<{
  store: EntryListStore
  beforeConfigChange?: (change: { key: "page" | "perPage"; newValue: number; oldValue: number }) => Promise<boolean> | boolean
}>()

const store = toRef(props, "store")

const firstEntryIndex = computed(() => {
  return (store.value.pagination.page - 1) * store.value.pagination.perPage + 1
})

const lastEntryIndex = computed(() => {
  return Math.min(store.value.pagination.totalCount, store.value.pagination.page * store.value.pagination.perPage)
})

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(store.value.pagination.totalCount / store.value.pagination.perPage))
})

const pageItems = computed(() => {
  return Array.from(Array(totalPages.value).keys()).map((i) => i + 1)
})

async function setPage(newValue: number) {
  const oldValue = store.value.pagination.page
  if (newValue === oldValue) {
    return
  }
  let proceed = true
  if (props.beforeConfigChange) {
    const result = props.beforeConfigChange({ key: "page", newValue, oldValue })
    proceed = result instanceof Promise ? await result : result
  }
  if (!proceed) {
    return
  }
  store.value.pagination.page = newValue
}

async function setPerPage(newValue: number) {
  const oldValue = store.value.pagination.perPage
  if (newValue === oldValue) {
    return
  }
  let proceed = true
  if (props.beforeConfigChange) {
    const result = props.beforeConfigChange({ key: "perPage", newValue, oldValue })
    proceed = result instanceof Promise ? await result : result
  }
  if (!proceed) {
    return
  }
  store.value.pagination.perPage = newValue
}

const nextPage = () => {
  const target = Math.min(totalPages.value, store.value.pagination.page + 1)
  void setPage(target)
}

const prevPage = () => {
  const target = Math.max(1, store.value.pagination.page - 1)
  void setPage(target)
}
</script>

<style lang="scss" scoped>
.s-pagination {
  display: flex;
  padding: 12px;
  gap: 32px;
  justify-content: flex-end;
}

.s-pagination__entry {
  display: flex;
  align-items: center;
  gap: 12px;
}

.s-pagination__select {
  width: 90px;
}
</style>
